import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { LoginPage, ThemedCard, UserPreferencesProvider, UserPreferenceThemeProvider, useUserPreferences } from '@satsankalpa-foundation-inc/ui';
import ActivityTracker from './pages/ActivityTracker';
import ExecutiveOverview from './components/ExecutiveOverview';
import Layout from './components/Layout';
import { portalAppsUrl } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiFetch } from './utils/api';
import ManagementPage from './pages/ManagementPage';

const loadPreferences = () => apiFetch('/api/v1/users/me/preferences');
const savePreferences = (preferences: unknown, baseRevision: number) => apiFetch('/api/v1/users/me/preferences', {
  method: 'PUT',
  body: JSON.stringify({ preferences, baseRevision }),
});

function RequireSession() {
  const { user, isLoading, error, refresh, mode, signup } = useAuth();
  if (isLoading) return null;
  if (error) return <div className="p-8" role="alert">{error}. Reload to retry.</div>;
  if (!user) {
    if (mode === 'standalone') return <LoginPage appName="Satsankalpa Operations" bannerTitle="Operations"
      showSignupOption={signup} minimumPasswordLength={12} onLoginSuccess={() => { void refresh(); }} />;
    return (
      <div className="mx-auto max-w-lg p-8">
        <ThemedCard className="space-y-4 p-6">
          <h1 className="text-xl font-bold">Open Operations from the Portal</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">{error || 'A valid Portal entitlement is required.'}</p>
          <a className="inline-flex rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white" href={portalAppsUrl}>Return to Portal</a>
        </ThemedCard>
      </div>
    );
  }
  return <Outlet />;
}

function ApplicationRoutes() {
  const { user } = useAuth();
  return (
    <UserPreferencesProvider
      userId={user?.id || null}
      applicationId="operations"
      loadRemote={user ? loadPreferences : undefined}
      saveRemote={user ? savePreferences : undefined}
      cacheKeyPrefix="operations_user_preferences"
    >
      <UserPreferenceThemeProvider>
        <Routes>
          <Route element={<RequireSession />}>
            <Route element={<Layout />}>
              <Route path="/tracker" element={<ActivityTracker />} />
              <Route path="/executive" element={<ExecutiveOverview />} />
              <Route path="/management" element={<ManagementPage />} />
              <Route path="/admin/accounts" element={<Navigate to="/management?tab=users" replace />} />
              <Route path="/" element={<PreferredLanding />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </UserPreferenceThemeProvider>
    </UserPreferencesProvider>
  );
}

function PreferredLanding(){
  const {applicationPreferences,isHydrated}=useUserPreferences();
  if(!isHydrated)return <div className="p-8 text-sm text-slate-500">Loading preferences…</div>;
  const preferred=applicationPreferences.navigation?.landingPage;
  return <Navigate to={preferred==='/executive'?'/executive':'/tracker'} replace />;
}

export default function App() {
  return <BrowserRouter><AuthProvider><ApplicationRoutes /></AuthProvider></BrowserRouter>;
}
